import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";

/**
 * Public page behind the clinic QR door sign. No authentication.
 *
 * The API only ever returns a currently-approved clinic and answers everything
 * else with a generic 404, so this page has exactly two states: verified, or
 * "not verified". It must never surface why a lookup failed — an unknown id, a
 * rejected clinic and a draft one all look identical here by design.
 */

const GOVERNORATE_DISPLAY: Record<string, string> = {
  baghdad: "Baghdad",
  basra: "Basrah",
  nineveh: "Nineveh",
  maysan: "Maysan",
  diyala: "Diyala",
  anbar: "Al Anbar",
  kirkuk: "Kirkuk",
  saladin: "Saladin",
  dohuk: "Duhok",
  erbil: "Erbil",
  sulaymaniyah: "Sulaymaniyah",
  halabja: "Halabja",
  najaf: "Najaf",
  karbala: "Karbala",
  babil: "Babylon",
  wasit: "Wasit",
  qadisiyah: "Al Qadisiyah",
  muthanna: "Al Muthanna",
  dhi_qar: "Dhiqar",
};

interface PublicClinic {
  id: string;
  name: string;
  name_ar?: string | null;
  status: string;
  registration_number?: string | null;
  license_number?: string | null;
  license_issued_date?: string | null;
  license_expiry_date?: string | null;
  governorate?: string | null;
  area?: { name?: string; name_ar?: string } | null;
  neighborhood?: number | null;
  street?: number | null;
  house_number?: number | null;
  nearest_landmark?: string | null;
  clinic_work_hours?: "morning" | "evening" | null;
  doctor?: {
    full_name?: string | null;
    full_name_ar?: string | null;
    medical_license_number?: number | null;
    specialization?: { name?: string; name_ar?: string } | null;
    practition?: { name?: string; name_ar?: string } | null;
    profile_photo_url?: string | null;
  } | null;
}

const PublicClinicVerifyScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [clinic, setClinic] = useState<PublicClinic | null>(null);
  const [notVerified, setNotVerified] = useState(false);

  const { t, isRTL, language } = useLanguage();
  const { clinicId } = useParams<{ clinicId: string }>();

  const loadClinic = useCallback(async () => {
    if (!clinicId) {
      setNotVerified(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setNotVerified(false);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8090";
      const response = await fetch(`${baseUrl}/clinic/public/${clinicId}`);

      if (!response.ok) {
        // Any failure — 404, 5xx, malformed id — collapses to the same message.
        setNotVerified(true);
        setLoading(false);
        return;
      }

      const result = await response.json();
      setClinic(result.data || result);
    } catch {
      setNotVerified(true);
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    loadClinic();
  }, [loadClinic]);

  const localized = (en?: string | null, ar?: string | null) =>
    (language === "ar" ? ar || en : en || ar) || undefined;

  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value: string | number | null | undefined;
  }) => {
    if (value === null || value === undefined || value === "") return null;
    return (
      <div className="flex justify-between items-start gap-3 py-2 border-b border-gray-100">
        <span className="text-sm text-gray-600 font-medium shrink-0">{label}</span>
        <span className="text-sm text-gray-900 font-semibold" dir="auto">
          {value}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  if (notVerified || !clinic) {
    return (
      <ResponsiveFormLayout
        showLanguageToggle={true}
        icon="/ima_logo.png"
        iconSize="w-24 h-24"
        title={t("clinic_verification")}
        maxHeight="90vh"
      >
        <div className="w-full space-y-6">
          <div className="p-5 rounded-xl border border-red-200 bg-red-50 text-center">
            <div className="text-5xl mb-3">❌</div>
            <p className="text-base font-semibold text-red-900 mb-2">
              {t("clinic_not_verified")}
            </p>
            <p className="text-sm text-red-800">{t("clinic_not_verified_message")}</p>
          </div>
          <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 text-center">
            <p className="text-xs text-gray-600">{t("public_view_footer")}</p>
          </div>
        </div>
      </ResponsiveFormLayout>
    );
  }

  const doctorName = localized(clinic.doctor?.full_name, clinic.doctor?.full_name_ar);
  const clinicName = localized(clinic.name, clinic.name_ar);
  const governorate = clinic.governorate
    ? t(GOVERNORATE_DISPLAY[clinic.governorate] || clinic.governorate)
    : null;
  // Morning/evening shift, so patients scanning the door sign know when the
  // clinic is open. Requests that predate the field declare none, and the row
  // hides itself rather than showing the raw enum.
  const workHours = clinic.clinic_work_hours ? t(clinic.clinic_work_hours) : null;

  return (
    <ResponsiveFormLayout
      showLanguageToggle={true}
      icon="/ima_logo.png"
      iconSize="w-24 h-24"
      title={t("clinic_verification")}
      subtitle={t("iraqi_medical_association")}
      maxHeight="90vh"
    >
      <div className="w-full space-y-6">
        {/* Approval banner */}
        <div className="p-5 rounded-xl border border-green-200 bg-green-50 text-center">
          <div className="text-5xl mb-3">✅</div>
          <p className="text-base font-bold text-green-900">{t("clinic_approved_verified")}</p>
          <p className="text-sm text-green-800 mt-1">{t("clinic_approved_verified_message")}</p>
        </div>

        {/* Doctor */}
        {clinic.doctor && (
          <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-cyan-50 to-blue-50">
            <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              👨‍⚕️ {t("doctor_information")}
            </h3>
            <div className="bg-white p-4 rounded-lg">
              <div className="flex items-start gap-4">
                {clinic.doctor.profile_photo_url && (
                  <img
                    src={clinic.doctor.profile_photo_url}
                    alt={doctorName || ""}
                    className="w-24 h-24 rounded-full object-cover border-4 border-[#2E5D7D] flex-shrink-0"
                  />
                )}
                <div className="flex-1 space-y-1">
                  <InfoRow label={t("doctor_name")} value={doctorName} />
                  <InfoRow
                    label={t("medical_license_number")}
                    value={clinic.doctor.medical_license_number}
                  />
                  <InfoRow
                    label={t("specialization")}
                    value={localized(
                      clinic.doctor.specialization?.name,
                      clinic.doctor.specialization?.name_ar
                    )}
                  />
                  <InfoRow
                    label={t("practition")}
                    value={localized(
                      clinic.doctor.practition?.name,
                      clinic.doctor.practition?.name_ar
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Clinic + official approval references */}
        <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
            🏥 {t("clinic_information")}
          </h3>
          <div className="space-y-1 bg-white p-4 rounded-lg">
            <InfoRow label={t("clinic_name")} value={clinicName} />
            <InfoRow label={t("registration_number")} value={clinic.registration_number} />
            <InfoRow label={t("clinic_opening_license_number")} value={clinic.license_number} />
            <InfoRow label={t("work_hours")} value={workHours} />
            <InfoRow
              label={t("license_issued_date")}
              value={
                clinic.license_issued_date
                  ? new Date(clinic.license_issued_date).toLocaleDateString()
                  : null
              }
            />
            <InfoRow
              label={t("license_expiry_date")}
              value={
                clinic.license_expiry_date
                  ? new Date(clinic.license_expiry_date).toLocaleDateString()
                  : null
              }
            />
          </div>
        </div>

        {/* Address */}
        <div className="p-5 rounded-xl border border-gray-200 bg-gradient-to-br from-purple-50 to-pink-50">
          <h3 className={`text-lg font-bold mb-4 text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
            📍 {t("location_information")}
          </h3>
          <div className="space-y-1 bg-white p-4 rounded-lg">
            <InfoRow label={t("governorate")} value={governorate} />
            <InfoRow
              label={t("city")}
              value={localized(clinic.area?.name, clinic.area?.name_ar)}
            />
            <InfoRow label={t("neighborhood")} value={clinic.neighborhood} />
            <InfoRow label={t("street")} value={clinic.street} />
            <InfoRow label={t("building_number")} value={clinic.house_number} />
            <InfoRow label={t("nearest_landmark")} value={clinic.nearest_landmark} />
          </div>
        </div>

        <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 text-center">
          <p className="text-xs text-gray-600">{t("public_view_footer")}</p>
        </div>
      </div>
    </ResponsiveFormLayout>
  );
};

export default PublicClinicVerifyScreen;
